import React, {
  useState,
  useEffect,
  useRef,
  FormEvent,
  ClipboardEvent,
  KeyboardEvent,
  ChangeEvent,
} from "react";
import { ArrowRight, MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BaseApi from "../services/base-api";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_NAME } from "../constants/info";
import { useCurrency } from "../components/Currency/CurrencyContext";
import { CURRENCIES } from "../components/Currency/Currencies";
import AppLogo from "../components/AppLogo";
import { COUNTRY_CODES } from "../constants/countryCodes";

type OtpChannel = "sms" | "whatsapp";

const normalizeOtpChannel = (value: unknown): OtpChannel | null => {
  if (typeof value !== "string") return null;

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue === "sms" || normalizedValue === "whatsapp"
    ? normalizedValue
    : null;
};

const extractOtpChannel = (payload: any): OtpChannel | null => {
  const data = payload?.data ?? payload;
  const directValue = normalizeOtpChannel(
    data?.otp_channel ?? data?.otpChannel,
  );

  if (directValue) return directValue;

  const settings = Array.isArray(data)
    ? data
    : Array.isArray(data?.settings)
      ? data.settings
      : [];
  const otpSetting = settings.find(
    (setting: any) => setting?.key === "otp_channel",
  );

  return normalizeOtpChannel(otpSetting?.value);
};

const SignIn: React.FC = () => {
  const OTP_LENGTH = 6;

  const navigate = useNavigate();
  const location = useLocation();
  const [countryCode, setCountryCode] = useState<string>("+974");
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [showCountryDropdown, setShowCountryDropdown] =
    useState<boolean>(false);
  const [searchCountry, setSearchCountry] = useState<string>("");
  const [errors, setErrors] = useState<{ mobile: string }>({ mobile: "" });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewUser, setIsNewUser] = useState<boolean>(false);
  const [pendingIsNewUser, setPendingIsNewUser] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [nameErrors, setNameErrors] = useState<{
    firstName: string;
    lastName: string;
  }>({ firstName: "", lastName: "" });
  const [showOTP, setShowOTP] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState<boolean>(false);
  const [otpChannel, setOtpChannel] = useState<OtpChannel>("whatsapp");
  const { currency, setCurrency } = useCurrency();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { t, i18n } = useTranslation();

  const resolveRedirectTarget = (from: unknown, fallback = "/") => {
    if (!from) return fallback;

    if (typeof from === "string") {
      return from.startsWith("/signin") || from.startsWith("/signup")
        ? fallback
        : from;
    }

    if (typeof from === "object") {
      const candidate = from as {
        pathname?: string;
        search?: string;
        hash?: string;
      };
      const pathname = candidate.pathname || fallback;
      const search = candidate.search || "";
      const hash = candidate.hash || "";
      const target = `${pathname}${search}${hash}`;
      return target.startsWith("/signin") || target.startsWith("/signup")
        ? fallback
        : target;
    }

    return fallback;
  };

  // Use AuthContext
  const {
    sendPhoneOtp,
    verifyPhoneOtp,
    resendPhoneOtp,
    isAuthenticated,
    isLoading: authLoading,
    updateUser,
  } = useAuth();

  useEffect(() => {
    const fetchOtpChannelSetting = async () => {
      try {
        const res = await BaseApi.get("/settings/public");
        const nextOtpChannel = extractOtpChannel(res);

        if (nextOtpChannel) {
          setOtpChannel(nextOtpChannel);
        }
      } catch (error) {
        console.error("Failed to fetch OTP channel setting:", error);
      }
    };

    fetchOtpChannelSetting();
  }, []);

  useEffect(() => {
    const detectCountryFromIP = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();

        // data.country_code => "QA", "IN", "US", etc.
        const matchedCountry = COUNTRY_CODES.find(
          (c) => c.country === data.country_code,
        );

        if (matchedCountry) {
          setCountryCode(matchedCountry.code);
        }
        const matchedCurrency = CURRENCIES.find(
          (c) => c.tag === data.country_code,
        );
        // console.log('matchedCountry', matchedCountry)
        // console.log('matchedCurrency', matchedCurrency)
        if (matchedCurrency) {
          setCurrency(matchedCurrency);
        }
      } catch (error) {
        console.error("IP country detection failed", error);
        // fallback remains +974
      }
    };

    detectCountryFromIP();
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated && !isNewUser && !isVerifyingOtp) {
      const target = resolveRedirectTarget(location.state?.from);
      navigate(target, { replace: true });
    }
  }, [
    authLoading,
    isAuthenticated,
    isNewUser,
    isVerifyingOtp,
    navigate,
    location.state,
  ]);

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (showOTP && resendTimer === 0) {
      setCanResend(true);
    }
  }, [resendTimer, showOTP]);

  // Auto-detect country code from mobile input
  useEffect(() => {
    if (mobileNumber.startsWith("+")) {
      const matchedCountry = COUNTRY_CODES.find((c) =>
        mobileNumber.startsWith(c.code),
      );
      if (matchedCountry) {
        setCountryCode(matchedCountry.code);
        setMobileNumber(mobileNumber.substring(matchedCountry.code.length));
      }
    }
  }, [mobileNumber]);

  const filteredCountries = COUNTRY_CODES.filter(
    (country) =>
      country.name.toLowerCase().includes(searchCountry.toLowerCase()) ||
      country.code.includes(searchCountry) ||
      country.country.toLowerCase().includes(searchCountry.toLowerCase()),
  );

  const validateMobile = (): boolean => {
    if (!mobileNumber) {
      setErrors({ mobile: t("Mobile number is required") });
      return false;
    }
    if (!/^[0-9]{6,15}$/.test(mobileNumber)) {
      setErrors({ mobile: t("Enter a valid mobile number") });
      return false;
    }
    setErrors({ mobile: "" });
    return true;
  };

  const validateNames = (): boolean => {
    const errors = { firstName: "", lastName: "" };
    let isValid = true;

    if (!firstName.trim()) {
      errors.firstName = t("First name is required");
      isValid = false;
    }

    if (!lastName.trim()) {
      errors.lastName = t("Last name is required");
      isValid = false;
    }

    setNameErrors(errors);
    return isValid;
  };

  const handleGetOTP = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateMobile()) return;

    setIsLoading(true);
    setErrors({ mobile: "" });

    try {
      setPendingIsNewUser(false);
      const res: any = await sendPhoneOtp(
        mobileNumber,
        countryCode.replace("+", ""),
        otpChannel,
      );
      setPendingIsNewUser(!!res?.data?.isNewUser);

      setShowOTP(true);
      setResendTimer(30);
      setCanResend(false);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } catch (error: any) {
      console.error("Send OTP error:", error);
      setErrors({
        mobile: error?.message || t("Failed to send OTP. Please try again."),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fillOtpFromText = (text: string) => {
    const digits = text.replace(/\D/g, "").slice(0, OTP_LENGTH);

    if (!digits) return;

    const newOtp = digits
      .split("")
      .concat(Array(OTP_LENGTH).fill(""))
      .slice(0, OTP_LENGTH);

    setOtp(newOtp);
    setOtpError("");

    const lastIndex = Math.min(digits.length, OTP_LENGTH - 1);
    inputRefs.current[lastIndex]?.focus();

    if (digits.length === OTP_LENGTH) {
      handleVerifyOTP(digits);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, "");

    // Mobile OTP autofill can inject all digits into the first box.
    if (digits.length > 1) {
      fillOtpFromText(digits);
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = digits;
    setOtp(newOtp);
    setOtpError("");

    if (digits && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (index === OTP_LENGTH - 1 && digits) {
      handleVerifyOTP(newOtp.join(""));
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    fillOtpFromText(e.clipboardData.getData("text"));
  };

  const handleVerifyOTP = async (otpValue?: string) => {
    const otpString = otpValue || otp.join("");

    if (otpString.length !== OTP_LENGTH) {
      setOtpError(t("Please enter all 6 digits"));
      return;
    }

    setIsLoading(true);
    setIsVerifyingOtp(true);
    setOtpError("");

    try {
      const res: any = await verifyPhoneOtp(
        otpString,
        mobileNumber,
        countryCode.replace("+", ""),
      );

      // Check if this is a new user
      const isFirstTime =
        res?.data?.isNewUser !== undefined
          ? !!res.data.isNewUser
          : pendingIsNewUser;
      if (isFirstTime) {
        setIsNewUser(true);
        setShowOTP(false);
      }
    } catch (error: any) {
      console.error("Verify OTP error:", error);
      setOtpError(error?.message || t("Invalid OTP. Please try again."));
      setOtp(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifyingOtp(false);
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setIsLoading(true);
    setOtpError("");

    try {
      await resendPhoneOtp(
        mobileNumber,
        countryCode.replace("+", ""),
        otpChannel,
      );
      setResendTimer(30);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } catch (error: any) {
      console.error("Resend OTP error:", error);
      setOtpError(
        error?.message || t("Failed to resend OTP. Please try again."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setShowOTP(false);
    setOtp(["", "", "", "", "", ""]);
    setResendTimer(0);
    setCanResend(false);
    setOtpError("");
    setSuccessMessage("");
    setPendingIsNewUser(false);
  };

  const handleNavigation = (route: string) => {
    navigate(`/${route}`);
  };

  const handleCompleteProfile = async () => {
    if (!validateNames()) return;

    setIsLoading(true);
    setNameErrors({ firstName: "", lastName: "" });

    try {
      // Call your API to update user profile
      const response = await BaseApi.put("/users/profile", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobileNumber,
        countryCode: countryCode.replace("+", ""),
      });

      if (response.success && response.data) {
        updateUser({
          ...response.data,
          firstName: response.data.firstName || firstName.trim(),
          lastName: response.data.lastName || lastName.trim(),
        });
        setSuccessMessage(`Welcome, ${firstName} ${lastName}!`);
        setTimeout(() => {
          // console.log("Navigating to dashboard...");
          // Add your navigation logic here
          const target = resolveRedirectTarget(location.state?.from);
          navigate(target, { replace: true });
        }, 1500);
      }
    } catch (error: any) {
      console.error("Update profile error:", error);
      setNameErrors({
        firstName: error?.message || t("Failed to update profile"),
        lastName: "",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode);
  const isSmsOtpChannel = otpChannel === "sms";

  // Render name fields for new users
  if (isNewUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-6">
        <style>{`
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-6">
              {/* Logo */}
              <AppLogo className="w-32 h-32 bg-white rounded-2xl lg-shadow" />
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              {t("Complete Your Profile")}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              {t("Please provide your name to continue")}
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-700">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-sm font-medium">{successMessage}</span>
            </div>
          )}

          {/* Form */}
          <div className="space-y-6">
            {/* First Name Field */}
            <div
              style={{
                animation: "slideInRight 0.5s ease-out 0.2s backwards",
              }}
            >
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("First Name")}
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFirstName(e.target.value);
                  if (nameErrors.firstName) {
                    setNameErrors({ ...nameErrors, firstName: "" });
                  }
                }}
                placeholder="Enter first name"
                className={`w-full px-4 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                  nameErrors.firstName
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none`}
                disabled={isLoading}
                autoFocus
              />
              {nameErrors.firstName && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {nameErrors.firstName}
                </p>
              )}
            </div>

            {/* Last Name Field */}
            <div
              style={{
                animation: "slideInRight 0.5s ease-out 0.3s backwards",
              }}
            >
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Last Name")}
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setLastName(e.target.value);
                  if (nameErrors.lastName) {
                    setNameErrors({ ...nameErrors, lastName: "" });
                  }
                }}
                placeholder="Enter last name"
                className={`w-full px-4 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                  nameErrors.lastName
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none`}
                disabled={isLoading}
              />
              {nameErrors.lastName && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {nameErrors.lastName}
                </p>
              )}
            </div>

            {/* Continue Button */}
            <div
              style={{
                animation: "slideInRight 0.5s ease-out 0.5s backwards",
              }}
            >
              <button
                onClick={handleCompleteProfile}
                disabled={isLoading || !firstName.trim() || !lastName.trim()}
                className={`w-full bg-black text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 text-base ${
                  isLoading || !firstName.trim() || !lastName.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-700 hover:shadow-2xl transform hover:-translate-y-1 active:scale-95"
                }`}
              >
                {isLoading ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    ></div>
                    <span>{t("Continuing..")}.</span>
                  </>
                ) : (
                  <>
                    <span>{t("Continue")}</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-500 leading-relaxed">
              {t("by-continue")}{" "}
              <button
                type="button"
                // onClick={() => handleNavigation("terms")} // TODO: Lets discuss this
                className="text-black font-semibold hover:underline"
              >
                {t("Terms")}
              </button>{" "}
              {t("and")}{" "}
              <button
                type="button"
                onClick={() => handleNavigation("privacy-policy")}
                className="text-black font-semibold hover:underline"
              >
                {t("Privacy Policy")}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render main sign-in flow
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-0 min-h-screen lg:min-h-[600px]">
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
            20%, 40%, 60%, 80% { transform: translateX(8px); }
          }
        `}</style>

        {/* Left Side - Branding Section */}
        <div
          className="hidden lg:flex bg-gradient-to-br from-black via-gray-700 to-black p-12 flex-col justify-center items-center relative overflow-hidden"
          style={{ animation: "slideInLeft 0.8s ease-out" }}
        >
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div
              className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"
              style={{ animation: "float 6s ease-in-out infinite" }}
            ></div>
            <div
              className="absolute top-40 right-20 w-20 h-20 border-2 border-white rounded-full"
              style={{ animation: "float 8s ease-in-out infinite 1s" }}
            ></div>
            <div
              className="absolute bottom-20 left-20 w-40 h-40 border-2 border-white rounded-full"
              style={{ animation: "float 7s ease-in-out infinite 2s" }}
            ></div>
            <div
              className="absolute bottom-40 right-10 w-24 h-24 border-2 border-white rounded-full"
              style={{ animation: "float 9s ease-in-out infinite 1.5s" }}
            ></div>
          </div>

          <div className="relative z-10 text-center">
            <div
              className="flex justify-center mb-8"
              style={{ animation: "pulse 2s ease-in-out infinite" }}
            >
              <AppLogo className="w-32 h-32 bg-white rounded-2xl lg-shadow" />
            </div>

            <h1 className="text-5xl font-bold text-white mb-4">{APP_NAME}</h1>
            <p
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
              className={`text-gray-300 text-lg mb-8 max-w-md ${
                i18n.language === "ar" ? "text-right" : "text-left"
              }`}
            >
              {t(
                "Premium quality products crafted with excellence. Your trusted partner in luxury.",
              )}
            </p>

            <div
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
              className={`space-y-4 ${
                i18n.language === "ar" ? "text-right" : "text-left"
              } max-w-md`}
            >
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Premium Quality")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Handpicked products with the finest materials")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Secure Shopping")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Your data is safe and encrypted")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Fast Delivery")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Quick and reliable shipping worldwide")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div
          className="flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-white"
          style={{ animation: "slideInRight 0.8s ease-out" }}
        >
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-8">
              {/* Logo */}
              <AppLogo className="w-32 h-32 bg-white rounded-2xl lg-shadow" />
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-700">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-sm font-medium">{successMessage}</span>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {showOTP ? t("Enter OTP") : t("welcome")}
              </h2>
              <p className="text-gray-600 text-base sm:text-lg">
                {showOTP
                  ? t(
                      isSmsOtpChannel
                        ? "enter-6-digit-code-sms"
                        : "enter-6-digit-code",
                    )
                  : t("enter-mobile-number")}
              </p>
              {showOTP && (
                <p className="text-sm text-gray-500 mt-2">
                  {selectedCountry?.flag} {countryCode} {mobileNumber}
                  {" • "}
                  <button
                    onClick={handleChangeNumber}
                    className="text-black font-semibold hover:underline"
                    disabled={isLoading}
                  >
                    {t("Change")}
                  </button>
                </p>
              )}
            </div>

            {!showOTP ? (
              <form onSubmit={handleGetOTP} className="space-y-6">
                <div
                  style={{
                    animation: "slideInRight 0.5s ease-out 0.2s backwards",
                  }}
                >
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Mobile Number")}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setShowCountryDropdown(!showCountryDropdown)
                        }
                        className="flex items-center gap-2 px-2 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-black focus:outline-none transition-all duration-200 bg-white"
                      >
                        <span className="text-2xl">
                          {selectedCountry?.flag}
                        </span>
                        <span className="font-semibold">{countryCode}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${
                            showCountryDropdown ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {showCountryDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-80 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden">
                          <div className="p-3 border-b border-gray-200">
                            <input
                              type="text"
                              placeholder="Search country..."
                              value={searchCountry}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setSearchCountry(e.target.value)
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none"
                            />
                          </div>
                          <div className="overflow-y-auto max-h-80">
                            {filteredCountries.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setCountryCode(country.code);
                                  setShowCountryDropdown(false);
                                  setSearchCountry("");
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                              >
                                <span className="text-2xl">{country.flag}</span>
                                <span className="font-medium flex-1">
                                  {country.name}
                                </span>
                                <span className="text-gray-500 font-semibold">
                                  {country.code}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <input
                        type="tel"
                        value={mobileNumber}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setMobileNumber(value);
                        }}
                        placeholder={t("Enter mobile number")}
                        className={`w-full px-4 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                          errors.mobile
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-black"
                        } focus:outline-none`}
                        maxLength={15}
                      />
                    </div>
                  </div>
                  {errors.mobile && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {errors.mobile}
                    </p>
                  )}
                </div>

                <div
                  style={{
                    animation: "slideInRight 0.5s ease-out 0.5s backwards",
                  }}
                >
                  <button
                    type="submit"
                    disabled={isLoading || !mobileNumber}
                    className={`w-full bg-black text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 text-base ${
                      isLoading || !mobileNumber
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-700 hover:shadow-2xl transform hover:-translate-y-1 active:scale-95"
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <div
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                          style={{ animation: "spin 1s linear infinite" }}
                        ></div>
                        <span>{t("Sending OTP...")}</span>
                      </>
                    ) : (
                      <>
                        <span>{t("Get OTP")}</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div
                      className={`rounded-full p-4 shadow-lg ${
                        isSmsOtpChannel
                          ? "bg-gradient-to-br from-gray-700 to-black"
                          : "bg-gradient-to-br from-green-400 to-green-600"
                      }`}
                      style={{ animation: "pulse 2s ease-in-out infinite" }}
                    >
                      {isSmsOtpChannel ? (
                        <MessageSquare
                          className="w-12 h-12 text-white"
                          strokeWidth={2.25}
                        />
                      ) : (
                        <svg
                          className="w-12 h-12 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex gap-2 justify-center mb-4">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={index === 0 ? OTP_LENGTH : 1}
                        value={digit}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleOtpChange(index, e.target.value)
                        }
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                          handleOtpKeyDown(index, e)
                        }
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold border-2 rounded-xl transition-all duration-200 ${
                          otpError
                            ? "border-red-500 animate-[shake_0.5s_ease-in-out]"
                            : digit
                              ? "border-black bg-gray-50"
                              : "border-gray-200 focus:border-black"
                        } focus:outline-none`}
                        disabled={isLoading}
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        name={index === 0 ? "otp" : `otp-${index}`}
                      />
                    ))}
                  </div>

                  {otpError && (
                    <p className="text-red-500 text-sm text-center flex items-center justify-center gap-2 mt-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {otpError}
                    </p>
                  )}
                </div>

                <button
                  onClick={() => handleVerifyOTP()}
                  disabled={isLoading || otp.join("").length !== OTP_LENGTH}
                  className={`w-full bg-black text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 text-base ${
                    isLoading || otp.join("").length !== OTP_LENGTH
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-700 hover:shadow-2xl transform hover:-translate-y-1 active:scale-95"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <div
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      ></div>
                      <span>{t("Verifying...")}</span>
                    </>
                  ) : (
                    <>
                      <span>{t("Verify OTP")}</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="text-center">
                  {!canResend ? (
                    <p className="text-sm text-gray-600">
                      {t("Resend code in")}{" "}
                      <span className="font-bold text-black">
                        {resendTimer}s
                      </span>
                    </p>
                  ) : (
                    <button
                      onClick={handleResendOTP}
                      disabled={isLoading}
                      className="text-black font-semibold hover:underline text-sm disabled:opacity-50"
                    >
                      {t("Resend OTP")}
                    </button>
                  )}
                </div>
              </div>
            )}

            <div
              className="text-center mt-8"
              style={{ animation: "slideInRight 0.5s ease-out 0.8s backwards" }}
            >
              <p className="text-xs text-gray-500 leading-relaxed">
                {t("by-continue")}{" "}
                <button
                  type="button"
                  onClick={() => handleNavigation("terms")}
                  className="text-black font-semibold hover:underline"
                >
                  {t("Terms")}
                </button>{" "}
                {t("and")}{" "}
                <button
                  type="button"
                  onClick={() => handleNavigation("privacy-policy")}
                  className="text-black font-semibold hover:underline"
                >
                  {t("Privacy Policy")}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
